import React from 'react';

import { Inject } from '@odinjs/odin';

import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateOption, HPMSOnOptionClick, phoneNumberFormatter, resolveAddress, setVisibility, TokenUtils } from '@philips-emr/hpms-components';
import { AvatarAPI, CardAPI, DeckAPI, DeckValue, FieldAPI, LinkAPI, PanelAPI, Stamp, StampIcon, TextAPI } from '@philips-emr/tws-components';
import { Color, ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { applicationContext, foundationContext } from '@philips-emr/tws-foundation';

import { DADOS_CONTRATO, DADOS_PESSOAIS, HISTORICO_AUTORIZACOES } from '../../app/module-registry';
import { DashboardAliases as Aliases, FragmentNames } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { Routes } from '../../definitions/routes';
import { Operadora } from '../../interfaces/operadora-interface';
import { Pessoa } from '../../interfaces/pessoa-interface';
import { ReportService, ToastService } from '../../services';
import BaseMensalidadeController, { getMensalidadeIconColor, Mensalidade } from '../financeiro/base-mensalidade-controller';

import '../../styles/dashboard-style.scss';

@Controller({ fragment: FragmentNames.DASHBOARD })
export default class DashboardController extends BaseMensalidadeController {

  @Inject(Aliases.PANEL_AUTORIZACOES)
  private panelAutorizacoes: PanelAPI;
  @Inject(Aliases.PANEL_MENSALIDADE)
  private panelMensalidade: PanelAPI;
  @Inject(Aliases.CARD_USER)
  private cardUser: CardAPI;
  @Inject(Aliases.AVATAR_USER)
  private avatarUser: AvatarAPI;
  @Inject(Aliases.LINK_ANS)
  private linkAns: LinkAPI;
  @Inject(Aliases.LINK_NUCLEO_FISCALIZACAO_ANS)
  private linkNucleFiscalizacaoAns: LinkAPI;
  @Inject(Aliases.LINK_TELEFONE_OPERADORA)
  private linkTelefoneOperadora: LinkAPI;
  @Inject(Aliases.LINK_TELEFONE_ANS)
  private linkTelefoneAns: LinkAPI;
  @Inject(Aliases.LINK_EMAIL_ANS)
  private linkEmailAns: LinkAPI;
  @Inject(Aliases.TEXT_MENSALIDADE_TOTAL)
  private textMensalidadeTotal: TextAPI;

  private fieldsWidth: FieldAPI[] = [];
  private fieldsVisibility: FieldAPI[] = [];

  @OnMount(Aliases.FIELD_NOTA_FISCAL)
  onFieldNFMount({ target }: MountEvent): void {
    const api = target as FieldAPI;
    const { nrNotaFiscal } = api.getData();

    if (!nrNotaFiscal) {
      api.setVisible(false);
    }

  }

  @OnMount(Aliases.AVATAR_USER)
  onAvatarMount({ target }: MountEvent): void {
    const api = target as AvatarAPI;
    const user = foundationContext.getUser();
    if (user) {
      api.setTitle(user.name);
      api.setImage(user.image);
    }
  }

  @OnAfterFetch(Aliases.FETCH_CARD_PESSOA)
  onCardPessoaAfterFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Pessoa;

      data.cdUsuarioPlano && this.avatarUser.setSubtitle(data.cdUsuarioPlano);
      data.dtNascimento && (data.dtNascimento = `${MessageService.translate('beneficiary.data-nascimento')} ${format('date', data.dtNascimento, { type: 'shortDate' })}`);
      data.nrCpf && (data.nrCpf = `${MessageService.translate('beneficiary.cpf')} ${data.nrCpf}`);
      data.nrIdentidade && (data.nrIdentidade = `${MessageService.translate('beneficiary.rg')} ${data.nrIdentidade}`);

      const endereco = resolveAddress([data.dsEndereco, data.nrEndereco, data.dsMunicipio]);
      data.dsEndereco && (data.dsEndereco = `${MessageService.translate('beneficiary.endereco')} ${endereco}`);
      data.nrTelefoneCelular = data.nrTelefoneCelular || data.nrTelefone || MessageService.translate('beneficiary.nao-informado');
    }
  }

  @OnAfterFetch(Aliases.FETCH_CARD_OPERADORA)
  onCardOperadoraFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Operadora;

      data.dsSiteAns && this.linkAns.setState({ route: data.dsSiteAns });
      data.dsLinkNucleoFisc && this.linkNucleFiscalizacaoAns.setState({ route: data.dsLinkNucleoFisc });

      const telefoneOperadora = `${data.nrDddTelefoneOperadora}${data.nrTelefoneOperadora}`;
      this.linkTelefoneOperadora.setState({ route: `tel:${telefoneOperadora}` });
      data.nrTelefoneOperadora = phoneNumberFormatter(telefoneOperadora, data.nrDddTelefoneOperadora.startsWith('080') || !data.nrDddTelefoneOperadora) || '';

      this.linkTelefoneAns.setState({ route: `tel:${data.nrTelefoneAns}` });
      data.nrTelefoneAns = phoneNumberFormatter(data.nrTelefoneAns, true);

      this.linkEmailAns.setState({ route: `mailto:${data.dsEmailAns}` });
    }
  }

  @OnMount(Aliases.PANEL_MENSALIDADE)
  onPanelMensalidadeMount({ target }: MountEvent): void {
    const { extFinMensalidade } = TokenUtils.getAllowedFuncion();
    const api = target as PanelAPI;
    api.setVisible(extFinMensalidade);
    if (extFinMensalidade) {
      super.onMountFetchMensalidades();
    }
  }

  @OnMount(Aliases.CARD_USER)
  onCardUserMount({ target }: MountEvent): void {
    const { extFinMensalidade } = TokenUtils.getAllowedFuncion();
    if (extFinMensalidade) {
      const api = target as CardAPI;
      api.setWidth(4);
    }
  }

  @OnMount([Aliases.FIELD_TITULARIDADE, Aliases.FIELD_DATA_NASCIMENTO, Aliases.FIELD_EMAIL])
  onMountFields({ target }: MountEvent): void {
    const { extFinMensalidade } = TokenUtils.getAllowedFuncion();
    if (extFinMensalidade) {
      const api = target as FieldAPI;
      api.setWidth(12);
      this.fieldsWidth.push(api);
    }
  }

  @OnMount([Aliases.FIELD_CPF, Aliases.FIELD_RG, Aliases.FIELD_ENDERECO])
  onMountFieldsAdc({ target }: MountEvent): void {
    const { extFinMensalidade } = TokenUtils.getAllowedFuncion();
    const api = target as FieldAPI;
    api.setVisible(!extFinMensalidade);
    this.fieldsVisibility.push(api);
  }

  @OnDynamicContentRender(Aliases.DYNAMIC_STATUS_AUTORIZACAO)
  onDynamicStatusAutorizacaoRender({ data, render }: RenderEvent): void {
    const { ieStatus } = data as { ieStatus: number };

    const props: { [key: number]: {} } = {
      1: { icon: StampIcon.CHECK, color: Color.SAFETY_GREEN },
      2: { icon: StampIcon.WARNING, color: Color.SAFETY_ORANGE },
      3: { icon: StampIcon.CANCELED, color: Color.SAFETY_RED },
    };

    render(() => <Stamp {...props[ieStatus]} label={`@beneficiary.autorizacao-status/${ieStatus}`} />);
  }

  @OnMount(Aliases.PANEL_AUTORIZACOES)
  onPanelAutorizacoesMount({ target }: MountEvent): void {
    const { extAcessoAutorizacao } = TokenUtils.getAllowedFuncion();
    const api = target as PanelAPI;
    api.setVisible(extAcessoAutorizacao);
  }

  @OnDynamicContentRender(Aliases.DYNAMIC_SITUACAO_MENSALIDADE)
  onDynamicSituacaoMensalidadeRender(event: RenderEvent): void {
    super.onDynamicContentRender(event);

    const { data } = event;
    const { situacaoSuperscription } = data as Mensalidade;
    this.textMensalidadeTotal.setClassName(`c-tws-color-${getMensalidadeIconColor(situacaoSuperscription)}`);
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD_MENSALIDADES)
  onCreateOption = (event: HpmsCustomCardMenuRenderEvent<Mensalidade>): void => super.onCreateOption(event);

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD_MENSALIDADES)
  onOptionClick = (event: HpmsCustomCardEvent<Mensalidade>): void => super.onOptionClick(event);

  @OnClick(Aliases.LINK_USER_VER_MAIS)
  onLinkUserVerMaisClick = (): void => applicationContext.openModule(DADOS_PESSOAIS);

  @OnClick(Aliases.LINK_PLANO_VER_MAIS)
  onLinkPlanoVerMaisClick = (): void => applicationContext.openModule(DADOS_CONTRATO);

  @OnClick(Aliases.LINK_AUTORIZACOES_VER_MAIS)
  onLinkAutorizacoesVerMaisClick = (): void => applicationContext.openModule(HISTORICO_AUTORIZACOES);

  @OnClick(Aliases.LINK_ROL_PROCEDIMENTOS)
  onRolProcedimentosClick(): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    HttpService.get<string>(Endpoints.GET.ROL_PROCEDIMENTOS, { nrSeqBeneficiario })
      .then(({ body }) => body && window.open(body))
      .catch(response => ToastService.open(response));
  }

  @OnMount(Aliases.LINK_TABELA_PRECO)
  onTabelaPrecoMount({ target }: MountEvent): void {
    const api = target as LinkAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ReportService.print({
      cdGrupo: '9',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    })
      .then(({ event }) => api.onClick.listen(event))
      .catch(() => api.setEnabled(false));
  }

  @OnMount(Aliases.LINK_EXTRATO_UTILIZACAO)
  onExtratoUtilizacaoMount({ target }: MountEvent): void {
    const api = target as LinkAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();
    const param = {
      cdGrupo: '8',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    };

    void ReportService.print(param)
      .then(({ record }) => api.onClick.listen(() => ModalService.openDefault({
        closeable: true,
        contextData: {
          record,
          ...param,
        },
        fragment: FragmentNames.MODAL_EXTRATO_UTILIZACAO,
        theme: ThemeTonalRange.ULTRA_LIGHT,
        title: 'beneficiary.extrato-utilizacao',
      })))
      .catch(() => api.setEnabled(false));
  }

  @OnSelectItem(Aliases.DECK_AUTORIZACOES)
  onSelectItem({ item, target }: SelectItemEvent<DeckValue>): void {
    const api = target as DeckAPI;
    api.unselectAll();

    const { nrSequencia } = item;
    applicationContext.openModule(HISTORICO_AUTORIZACOES, {
      at: {
        path: Routes.DETALHES_AUTORIZACAO,
        params: { nrSeqGuia: nrSequencia as string },
      },
    });
  }

  @OnAfterFetch(Aliases.FETCH_AUTORIZACOES)
  onAfterFetchAutorizacoes = (event: FetchEvent): void => setVisibility(event, this.panelAutorizacoes);

  @OnAfterFetch(Aliases.FETCH_MENSALIDADE)
  onAfterFetchMensalidade({ data }: FetchEvent): void {
    if (data) {
      if (!Object.keys(data).length) {
        this.panelMensalidade.setVisible(false);
        this.cardUser.setWidth(8);
        this.fieldsWidth.forEach(field => field.setWidth(6));
        this.fieldsVisibility.forEach(field => field.setVisible(true));
      }
    }
  }

  @OnClick(Aliases.LINK_MOSTRAR_TODAS_MENSAGENS)
  onCLickTodasMensagens = (): void => this.historyService.push(Routes.MENSAGENS);
}
